import type { FC, ReactNode } from 'react'
import type { CategorySummary } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { iconFor } from '@/components/directory/category-icon'

interface CategoryNavProps {
    categories: CategorySummary[]
    activeKey?: string
}

const itemClass = 'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-primary'

export const CategoryNav: FC<CategoryNavProps> = ({ categories, activeKey }): ReactNode => {
    const extras = [
        { key: 'free-tools', href: '/free-tools', label: 'Free AI Tools' },
        { key: 'open-source', href: '/open-source', label: 'Open Source & Local' },
        { key: 'shut-down', href: '/shut-down', label: 'Shut Down' }
    ]

    const renderItem = (key: string, href: string, label: ReactNode) => {
        const Icon = iconFor(key)
        const active = key === activeKey

        return (
            <li key={key}>
                <Link
                    href={href}
                    aria-current={active ? 'page' : undefined}
                    className={cn(itemClass, active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary hover:text-foreground')}
                >
                    <Icon className='size-4 shrink-0' aria-hidden='true' />
                    {label}
                </Link>
            </li>
        )
    }

    return (
        <nav aria-label='Categories' className='rounded-2xl border border-border bg-card p-3'>
            <h2 className='px-3 pb-2 pt-1 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                Categories
            </h2>

            <ul className='space-y-0.5'>
                {categories.map(category => renderItem(
                    category.slug,
                    `/category/${category.slug}`,
                    <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                ))}
            </ul>

            <div className='mx-3 my-2 border-t border-border' />

            <ul className='space-y-0.5'>
                {extras.map(extra => renderItem(extra.key, extra.href, extra.label))}
            </ul>
        </nav>
    )
}
